import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationCenter from "@/components/telehealth/NotificationCenter";
import { ScanLine, ShieldCheck, BellOff, GraduationCap } from "lucide-react";
import { getEnrollment } from "@/lib/alertsApi";
import { IconSignOut, IconSettings, IconExperienceMode } from "@/components/icons/revqIcons";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Topbar() {
  const { user, signOut, token } = useAuth();
  const navigate = useNavigate();
  const initials =
    (user?.first_name?.[0] || user?.email?.[0] || "?").toUpperCase() +
    (user?.last_name?.[0] || "").toUpperCase();

  // Global "Upload" → open the Scan-with-Lens lightbox, pre-set to the chosen kind:
  // "note" = claim documents, "remittance" = EOB / remittances. AppShell honours the kind.
  const openUpload = (kind) => {
    window.dispatchEvent(new CustomEvent("revq:new-claim", { detail: { kind } }));
  };

  // Experience mode — same overlay events the side menu used; only its location changed.
  const [expOn, setExpOn] = useState(false);
  useEffect(() => {
    const onState = (e) => setExpOn(!!e.detail?.open);
    window.addEventListener("revq:experience-state", onState);
    return () => window.removeEventListener("revq:experience-state", onState);
  }, []);
  const toggleExperience = () => window.dispatchEvent(new Event(expOn ? "revq:close-experience" : "revq:open-experience"));

  // Alerts enrollment indicator — reads status, refreshes on the nudge's broadcast.
  const [alertStatus, setAlertStatus] = useState(null);
  useEffect(() => {
    let on = true;
    getEnrollment().then((d) => { if (on) setAlertStatus(d.status); }).catch(() => {});
    const h = (e) => setAlertStatus(e.detail?.status);
    window.addEventListener("revq:alerts-status", h);
    return () => { on = false; window.removeEventListener("revq:alerts-status", h); };
  }, []);
  const alertsOn = alertStatus === "verified";

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "Account";

  return (
    <div
      className="h-[57px] flex-shrink-0 flex items-center px-6 gap-2 relative z-[5] bg-white"
      style={{
        borderBottom: "1px solid #E9E9EC",
      }}
    >
      <style>{`
        .upl{display:inline-flex;align-items:center;height:32px;background:#141412;border-radius:8px;padding:3px;gap:2px}
        .upl-seg{display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:5px;font-size:12.5px;font-weight:600;color:#fff;white-space:nowrap;background:transparent;transition:background .12s cubic-bezier(.4,0,.2,1),color .12s cubic-bezier(.4,0,.2,1),opacity .12s cubic-bezier(.4,0,.2,1)}
        .upl-seg:hover{background:#fff;color:#141412}
        .upl-seg:active{background:#E9E9EC}
        .upl-seg:focus-visible{outline:2px solid #fff;outline-offset:1px}
        .upl-div{width:1px;align-self:stretch;margin:6px 0;background:rgba(255,255,255,.22);transition:opacity .12s cubic-bezier(.4,0,.2,1)}
        .upl:has(.upl-seg:hover) .upl-seg:not(:hover){opacity:.5}
        .upl:has(.upl-seg:hover) .upl-div{opacity:0}
      `}</style>

      <GlobalSearch />

      <div className="ml-auto flex items-center gap-2">
        <button
          data-testid="topbar-learning-center"
          onClick={() => window.open(`/LearningCenter${token ? `#t=${encodeURIComponent(token)}` : ""}`, "_blank", "noopener")}
          title="Learning Center — self-paced product training (opens in a new tab)"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white h-[32px] px-[12px] rounded-[8px] transition-colors"
          style={{ background: "#1F8A54" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1A7549")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1F8A54")}
        >
          <GraduationCap size={15} strokeWidth={2} /> Learning Center
        </button>
        {/* Upload bar — one ink bar, two buttons, single Lens mark */}
        <div className="upl" role="group" aria-label="Upload with Lens" data-testid="topbar-upload-bar">
          <span className="inline-flex items-center flex-none text-white" style={{ paddingLeft: 8, paddingRight: 6 }} aria-hidden="true">
            <ScanLine size={15} strokeWidth={1.75} />
          </span>
          <button className="upl-seg" data-testid="topbar-upload-claim" onClick={() => openUpload("note")} title="Upload to process: claim documents">Upload claims</button>
          <span className="upl-div" aria-hidden="true" />
          <button className="upl-seg" data-testid="topbar-upload-remittance" onClick={() => openUpload("remittance")} title="Upload to post: EOBs and remittances">Upload EOBs</button>
        </div>

        <NotificationCenter />

        <button
          data-testid="topbar-alerts-indicator"
          onClick={() => window.dispatchEvent(new Event("revq:open-alerts"))}
          title={alertsOn ? "Email alerts: on — click to manage" : "Email alerts: off — click to turn on"}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-[10px] h-[32px] rounded-[8px] border transition"
          style={alertsOn
            ? { color: "#047857", background: "#ECFDF5", borderColor: "#CBF0E0" }
            : { color: "var(--ink2)", background: "rgba(0,0,0,.05)", borderColor: "rgba(0,0,0,.07)" }}
        >
          {alertsOn ? <ShieldCheck size={14} /> : <BellOff size={14} />}
          {alertsOn ? "Alerts on" : "Alerts off"}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={TID.userAvatar}
              aria-label="Account menu"
              title="Account"
              className="w-[32px] h-[32px] rounded-full flex items-center justify-center text-[10px] font-bold text-white cursor-pointer transition-shadow hover:shadow-[0_0_0_3px_#E9E9EC] focus:outline-none data-[state=open]:shadow-[0_0_0_3px_#E9E9EC]"
              style={{
                background: "linear-gradient(145deg,#1a1a1a,#000)",
              }}
            >
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-[232px]" data-testid="topbar-account-menu">
            <DropdownMenuLabel className="px-2 py-1.5">
              <div className="text-[12px] font-semibold text-[#141412] truncate leading-tight">{fullName}</div>
              {user?.email ? <div className="text-[10.5px] font-normal text-[#9B9B97] truncate">{user.email}</div> : null}
            </DropdownMenuLabel>
            <DropdownMenuItem
              role="menuitemcheckbox"
              aria-checked={expOn}
              data-testid="account-menu-experience"
              onSelect={(e) => { e.preventDefault(); toggleExperience(); }}
              className="h-[34px] px-[10px] gap-[10px] cursor-pointer text-[13px] text-[#141412] focus:text-[#141412]"
            >
              <span className="flex-none text-[#6C6C69]"><IconExperienceMode size={15} /></span>
              <span className="flex-1 truncate">Experience mode</span>
              <span className="flex-none relative rounded-[9px]" style={{ width: 30, height: 18, background: expOn ? "#141412" : "#E9E9EC", transition: "background-color .2s cubic-bezier(.4,0,.2,1)" }} data-testid="account-menu-experience-switch">
                <span className="absolute rounded-full bg-white" style={{ width: 14, height: 14, top: 2, left: 2, transform: expOn ? "translateX(12px)" : "none", transition: "transform .2s cubic-bezier(.4,0,.2,1)", boxShadow: "0 1px 2px rgba(20,20,18,.2)" }} />
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="account-menu-settings"
              onClick={() => navigate("/settings", { state: { tab: "practice-setup" } })}
              className="gap-2 cursor-pointer text-[12.5px]"
            >
              <IconSettings size={15} stroke={1.9} /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-[4px] bg-[#E9E9EC]" />
            <DropdownMenuItem
              data-testid={TID.signOut}
              onClick={() => { signOut(); navigate("/login"); }}
              className="gap-2 cursor-pointer text-[12.5px] text-[#141412] focus:text-[#141412]"
            >
              <IconSignOut size={15} stroke={1.9} /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
